import type { ReactNode } from 'react';
import { ActionIcon } from '@repletoryreach/sdk-ui';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../i18n/I18nProvider';

export interface RowAction {
  /** Stable key for the entry. */
  id: string;
  label: ReactNode;
  onSelect: () => void;
  /** Renders in the error colour. Put these last, as the design does. */
  destructive?: boolean;
  /** Shown but not selectable, for an action whose endpoint does not exist yet. */
  disabled?: boolean;
}

/**
 * The `…` at the end of a row.
 *
 * Three buttons per row is what these tables looked like before, and it made
 * every row shout. A row that navigates on click can still carry three or four
 * other things a person might want to do with it, and the design puts them
 * here rather than spending a column on each.
 *
 * The trigger stops the click from reaching the row, because opening the menu
 * is not the same gesture as opening the record.
 */
export function RowMenu({ actions }: { actions: RowAction[] }) {
  const t = useT();
  if (actions.length === 0) return null;
  return (
    <Menu position="bottom-end" width={220}>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          size="sm"
          aria-label={t('table.rowActions')}
          onClick={(e) => e.stopPropagation()}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
            <circle cx="3" cy="8" r="1.4" />
            <circle cx="8" cy="8" r="1.4" />
            <circle cx="13" cy="8" r="1.4" />
          </svg>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {actions.map((a) => (
          <Menu.Item
            key={a.id}
            color={a.destructive ? 'error' : undefined}
            disabled={a.disabled}
            onClick={(e) => { e.stopPropagation(); a.onSelect(); }}
          >
            {a.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
